import { useEffect } from 'react';
import { useRecordContext, useRedirect } from 'react-admin';
import { Dialog } from '@/components/dialogs';
import { OverlaidLogoLoader } from '@/components/loaders';
import useCandidateTabs from '@/pages/Candidate/hooks/useCandidateTabs';
import CandidateTabbedShowLayoutTabs from '@/pages/Candidate/view/CandidateTabbedShowLayoutTabs';
import { prepareCandidateShowTabLayout } from '@/pages/Candidate/view/candidateShowTabSections';
import { path } from '@/pages/Candidate/view/constants';
import { TabbedShowLayoutStyled } from '@/pages/Candidate/view/styled';
import { FCRAWorker } from '@/types/turn-api';
import type { CandidateTab } from '@/pages/Candidate/hooks/useCandidateTabs/types';

const TAB_PATHS: CandidateTab[] = [
  'summary',
  'addresses',
  'checks',
  'one_off_checks',
  'cm_actions',
  'notes',
  'criminal_records',
  'sexoff_records',
  'national_records',
  'watchlist_records',
  'federal_records',
  'mvr',
  'run_mvr',
  'mvr_actions',
  'mvr_accidents',
  'mvr_violations',
  'mvr_license',
];

/** Redirect when URL splat points at a tab that is hidden for this candidate. */
const useRedirectHiddenTab = (shouldRenderTab: Record<CandidateTab, boolean>, recordId?: number | string) => {
  const redirect = useRedirect();

  useEffect(() => {
    const splat = window.location.pathname.split('/show/')[1]?.replace(/\/$/, '') ?? '';
    if (!splat || splat === 'summary') return;

    const tabKey = TAB_PATHS.find((tab) => path(tab) === splat);
    if (tabKey && !shouldRenderTab[tabKey] && recordId != null) {
      redirect('show', 'candidates', recordId);
    }
  }, [shouldRenderTab, recordId, redirect]);
};

const CandidateShowTabs = () => {
  const record = useRecordContext<FCRAWorker.Details>();
  const { shouldRenderTab } = useCandidateTabs(record);
  const { sections: tabSections, tabElements } = prepareCandidateShowTabLayout(shouldRenderTab, path);

  useRedirectHiddenTab(shouldRenderTab, record?.id);

  return (
    <>
      <Dialog />
      <TabbedShowLayoutStyled tabs={<CandidateTabbedShowLayoutTabs sections={tabSections} />}>
        {tabElements}
      </TabbedShowLayoutStyled>
    </>
  );
};

export default CandidateShowTabs;

export const CandidateShowLoading = () => (
  <>
    <Dialog />
    <OverlaidLogoLoader />
  </>
);
